/**
 * Utility functions for handling file operations in the Sales Tools module
 */

/**
 * Handles file clicks with smart behavior based on file type
 * - Viewable files (PDFs, images, videos): Opens in new tab for inline viewing
 * - Downloadable files (Excel, Word, PowerPoint): Downloads with correct filename
 */
export const handleFileClick = async (
  fileUrl: string, 
  filename: string
): Promise<void> => {
  try {
    // Use the existing isFileViewableInBrowser function to determine behavior
    const isViewable = isFileViewableInBrowser(filename);
    
    if (isViewable) {
      // For viewable files (PDFs, images, videos, text files), open in new tab for inline viewing
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    } else {
      // For downloadable files (Excel, Word, PowerPoint, etc.), use proper download mechanism
      await downloadFile(fileUrl, filename);
    }
    
  } catch (error) {
    console.error('Error handling file:', error);
    // Fallback: try to open in new tab
    window.open(fileUrl, '_blank', 'noopener,noreferrer');
  }
};

/**
 * Downloads a file using fetch API to ensure proper filename handling
 */
const downloadFile = async (fileUrl: string, filename: string): Promise<void> => {
  try {
    // Add cache-busting parameter
    const urlWithTimestamp = `${fileUrl}${fileUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
    
    const response = await fetch(urlWithTimestamp, {
      method: 'GET',
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const blob = await response.blob();
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up
    window.URL.revokeObjectURL(url);
    
  } catch (error) {
    console.error('Error downloading file:', error);
    // Fallback: try anchor element approach
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

/**
 * Determines if a file is viewable in the browser
 * This matches the backend logic for Content-Disposition: inline
 */
export const isFileViewableInBrowser = (filename: string): boolean => {
  const viewableExtensions = [
    // Documents that browsers can display inline
    '.pdf', '.txt', '.html', '.htm', '.xml', '.json',
    // Images that browsers can display inline
    '.jpg', '.jpeg', '.png', '.gif', '.svg', '.webp',
    // Videos that browsers can play inline
    '.mp4', '.webm', '.ogg', '.mp3', '.wav', '.avi', '.mov', '.wmv'
  ];
  
  const extension = filename.toLowerCase().substring(filename.lastIndexOf('.'));
  return viewableExtensions.includes(extension);
};
